import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	List,
	RadioGroup,
	Stack,
	Typography,
} from '@mui/material';
import {AlertInfo} from 'presentation/components';
import {ControlledRadioGroup} from 'presentation/components/ControlledRadioGroup';
import {Loading} from 'presentation/components/Loading/Loading';
import {DocumentEnum} from 'presentation/pages/private/refund/constants/documentEnum';
import {useSelectedProfessionalStore} from 'presentation/pages/private/refund/pages/Request/store/useSelectedProfessionalStore';
import {translations} from 'presentation/translations-new';
import React, {useState} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {DoctorListItem} from '../../../../DoctorListItem';
import {SEARCH_DOCTOR_BY_OPTS} from '../../constants/formOpts';
import {SearchDoctorByEnum} from '../../constants/types';
import {CouncilFields} from '../CouncilFields';
import {DoctorFields} from '../DoctorFields';
import {DocumentsFields} from '../DocumentFields';
import {SearchProfessionalType} from './types';

const {
	search,
	seeMore: seeMoreText,
	searchDoctor,
	invalidSelectProfessional,
} = translations['pt-br'].privateFlow.refund.receiptRegistrationPage.providerData;

const {requiredField} = translations['pt-br'].commons;

export const SearchProfessional = ({
	isLoadingProfessional,
	doctorsListProfessional,
	hasMore,
	handleAddMoreToList,
	isLoadingMore,
	onSubmit,
	onReset,
	onResetProfessionalList,
	searchDoctorByWatch,
	professionalNotFound,
	setPageProfessional,
}: SearchProfessionalType) => {
	const {
		control,
		setValue,
		trigger,
		formState: {errors},
	} = useFormContext();
	const selectedProfessional = useSelectedProfessionalStore(state => state.professional);
	const setSelectedProfessional = useSelectedProfessionalStore(state => state.setProfessional);

	const [shouldRegisterOtherProvider, setShouldRegisterOtherProvider] = useState(false);

	function handleChangeProfessional(event: React.ChangeEvent<HTMLInputElement>) {
		const selectedProfessionalDocument = event.target.value;
		const doctor = doctorsListProfessional?.find(
			item => `${item?.document}-${item?.providerCode}` === selectedProfessionalDocument,
		);
		setSelectedProfessional(doctor || null);
		setValue('professionalName', doctor?.performerName ?? doctor?.providerName);
		setValue('councilAcronym', doctor?.councilAcronym);
		setValue('councilNumber', doctor?.councilNumber);
		setValue('councilState', doctor?.councilState);
		setValue('professionalCode', doctor?.providerCode);
		trigger();
	}

	const professionalCpfHasError = !!errors?.professionalCpf;
	const helperTextErrorProfessionalCpf =
		typeof errors?.professionalCpf?.message === 'string'
			? errors.professionalCpf.message
			: undefined;

	const doctorSelectedHasError =
		errors?.professionalName &&
		!errors?.professionalCpf &&
		searchDoctorByWatch === SearchDoctorByEnum.DOCUMENT &&
		doctorsListProfessional?.length !== 0;

	return (
		<Stack
			gap={1}
			sx={{
				borderTop: theme => `1px solid ${theme.colors.gray.A10Percent}`,
				pt: '16px',
				mt: '16px',
			}}>
			<Typography color="primary" fontWeight={600}>
				{searchDoctor}
			</Typography>

			<Controller
				control={control}
				name="searchDoctorBy"
				render={({field: {value, onChange}, fieldState: {error}}) => (
					<Stack gap={1}>
						<ControlledRadioGroup
							gap="16px"
							direction="row"
							radioOpts={SEARCH_DOCTOR_BY_OPTS}
							key={value}
							value={value}
							onChange={e => {
								onChange(e);
								if (onReset) {
									onReset(true);
								}
							}}
						/>
						{error ? (
							<Typography variant="caption" color="error">
								{requiredField}
							</Typography>
						) : null}
					</Stack>
				)}
			/>

			{searchDoctorByWatch === SearchDoctorByEnum.DOCUMENT ? (
				<DocumentsFields
					documentName={DocumentEnum.CPF}
					handleReset={() => {
						if (onReset) {
							onReset(true);
						}
					}}
					customRegister="professionalCpf"
					error={professionalCpfHasError}
					helperText={helperTextErrorProfessionalCpf}
				/>
			) : (
				<CouncilFields
					handleReset={() => {
						if (onResetProfessionalList) {
							onResetProfessionalList(true);
						}
					}}
					searchInProvider
				/>
			)}
			{!professionalNotFound ? (
				<Button
					variant="outlined"
					color="primary"
					onClick={() => {
						onSubmit(true);
						setShouldRegisterOtherProvider(false);
						setPageProfessional(1);
					}}
					disabled={isLoadingProfessional}
					endIcon={
						isLoadingProfessional ? (
							<CircularProgress size={16} color="inherit" />
						) : null
					}>
					{search}
				</Button>
			) : (
				<DoctorFields
					personType={'physical-person'}
					doctorNotFound={professionalNotFound}
					searchDoctorByWatch={searchDoctorByWatch}
					registerOtherProviderFlow={shouldRegisterOtherProvider}
					searchInProvider
				/>
			)}

			<FormControl>
				{doctorSelectedHasError ? (
					<AlertInfo.Root severity="error" sx={{marginTop: '8px'}}>
						<AlertInfo.Description>
							<Typography variant="body2">{invalidSelectProfessional}</Typography>
						</AlertInfo.Description>
					</AlertInfo.Root>
				) : null}
				<List>
					{doctorsListProfessional ? (
						<RadioGroup
							aria-labelledby="demo-controlled-radio-buttons-group"
							name="controlled-radio-buttons-group"
							value={
								selectedProfessional
									? `${selectedProfessional?.document}-${selectedProfessional?.providerCode}`
									: ''
							}
							onChange={handleChangeProfessional}>
							{doctorsListProfessional?.map((doctor, index) => (
								<DoctorListItem
									key={`${doctor?.document}-${doctor?.providerCode}`}
									doctor={doctor}
									isLastItem={
										index === (doctorsListProfessional?.length ?? 1) - 1
									}
									selectedValue={
										selectedProfessional
											? `${selectedProfessional?.document}-${selectedProfessional?.providerCode}`
											: ''
									}
									onChange={handleChangeProfessional}
								/>
							))}
						</RadioGroup>
					) : null}
				</List>

				{hasMore ? (
					<Box width="100%" display="flex" justifyContent="center">
						<Button
							variant="text"
							onClick={handleAddMoreToList}
							disabled={isLoadingMore}
							endIcon={isLoadingMore ? <Loading size={16} color="inherit" /> : null}>
							{seeMoreText}
						</Button>
					</Box>
				) : null}
			</FormControl>
		</Stack>
	);
};
